"use client";

import { c as _c } from "react/compiler-runtime";
import type { ISbEventPayload, ISbStoryData } from "@storyblok/js";
import { type FC, type ReactNode, startTransition, useEffect, useRef, useState } from "react";
import { renderStoryPreview } from "@/storyblok/actions/render-story-preview";
import type { StoryData } from "@/storyblok/lib";
const INPUT_DEBOUNCE_MS = 250;
const STORYBLOK_BRIDGE_SCRIPT_URL = "https://app.storyblok.com/f/storyblok-v2-latest.js";
const STORYBLOK_BRIDGE_SELECTOR = 'script[data-storyblok-bridge="true"]';
export interface StoryPreviewProps {
  storyId: number;
  story: StoryData;
  pathname: string;
  children: ReactNode;
}
const toStoryData = (story: ISbStoryData | StoryData): StoryData => {
  return story as StoryData;
};
const isMatchingStory = (storyId: number, incomingStory: ISbStoryData | StoryData): boolean => {
  return typeof incomingStory.id === "number" && incomingStory.id === storyId;
};
const loadStoryblokBridgeScript = async (): Promise<void> => {
  if (window.StoryblokBridge) {
    return;
  }
  const existingScript = document.querySelector<HTMLScriptElement>(STORYBLOK_BRIDGE_SELECTOR);
  if (existingScript) {
    await new Promise<void>((resolve, reject) => {
      if (window.StoryblokBridge) {
        resolve();
        return;
      }
      const handleLoad = () => resolve();
      const handleError = () => reject(new Error("Failed to load Storyblok Bridge script."));
      existingScript.addEventListener("load", handleLoad, {
        once: true
      });
      existingScript.addEventListener("error", handleError, {
        once: true
      });
    });
    return;
  }
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = STORYBLOK_BRIDGE_SCRIPT_URL;
    script.async = true;
    script.dataset.storyblokBridge = "true";
    script.addEventListener("load", () => resolve(), {
      once: true
    });
    script.addEventListener("error", () => reject(new Error("Failed to load Storyblok Bridge script.")), {
      once: true
    });
    document.head.appendChild(script);
  });
};
export const StoryPreview: FC<StoryPreviewProps> = t0 => {
  const $ = _c(10);
  const {
    storyId,
    story,
    pathname,
    children
  } = t0;
  const [renderedPreview, setRenderedPreview] = useState(children);
  const latestStoryRef = useRef(story);
  const inputTimeoutRef = useRef(undefined);
  const isUnmountedRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== children) {
    t1 = () => {
      setRenderedPreview(children);
    };
    t2 = [children];
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== story) {
    t3 = () => {
      latestStoryRef.current = story;
    };
    t4 = [story];
    $[3] = story;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== pathname || $[7] !== storyId) {
    t5 = () => {
      isUnmountedRef.current = false;
      const updatePreview = async incomingStory => {
        const nextRender = await renderStoryPreview(incomingStory, pathname);
        if (isUnmountedRef.current || !nextRender) {
          return;
        }
        startTransition(() => {
          setRenderedPreview(nextRender);
        });
      };
      const scheduleInputUpdate = incomingStory_0 => {
        if (inputTimeoutRef.current) {
          window.clearTimeout(inputTimeoutRef.current);
        }
        inputTimeoutRef.current = window.setTimeout(() => {
          updatePreview(incomingStory_0);
        }, INPUT_DEBOUNCE_MS);
      };
      const initBridge = async () => {
        await loadStoryblokBridgeScript();
        if (!window.StoryblokBridge) {
          return;
        }
        const storyblokBridge = new window.StoryblokBridge({
          preventClicks: true
        });
        storyblokBridge.on("input", payload => {
          const bridgeStory = payload?.story;
          if (!bridgeStory || !isMatchingStory(storyId, bridgeStory)) {
            return;
          }
          const incomingStory_1 = toStoryData(bridgeStory);
          latestStoryRef.current = incomingStory_1;
          scheduleInputUpdate(incomingStory_1);
        });
        storyblokBridge.on(["change", "published"], payload_0 => {
          const action = payload_0?.action;
          const bridgeStory_0 = payload_0?.story;
          if (action !== "change" && action !== "published") {
            return;
          }
          if (bridgeStory_0 && !isMatchingStory(storyId, bridgeStory_0)) {
            return;
          }
          updatePreview(latestStoryRef.current);
        });
      };
      initBridge();
      return () => {
        isUnmountedRef.current = true;
        if (inputTimeoutRef.current) {
          window.clearTimeout(inputTimeoutRef.current);
        }
      };
    };
    t6 = [pathname, storyId];
    $[6] = pathname;
    $[7] = storyId;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  return renderedPreview;
};